import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  ContentCard,
  Row,
  Button,
  Chip,
  Alert,
  Radio,
} from "@narmi/design_system";
import { Dialog, FileUploader, useNotificationContext } from "cerulean";
import ApiHttp from "byzantine/src/ApiHttp";
import { extractErrorMessages } from "byzantine/src/utils";
import { modules } from "byzantine";
import { featureEnabled } from "byzantine/src/Feature";
import { canUseAchPassthrough } from "byzantine/src/dbbl/businessLogic/modules/features/paymentActivity";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import HeaderWithSteps from "../../HeaderWithSteps";
import Container from "../../Container";
import {
  extractFilenameFromResponse,
  triggerFileDownload,
} from "../../DownloadTransactionsDialog/transactionDownloadHelpers";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import { useACHImportContext } from "../ACHImportContext";
import { useACHPassthroughImportContext } from "../ACHPassthroughImportContext";
import { useDomesticWireImportContext } from "../WireFileImport";
import {
  MAX_ACH_IMPORT_FILE_SIZE_BYTES,
  MAX_CSV_IMPORT_FILE_SIZE_BYTES,
  CSV_EXTENSIONS,
  ALLOWED_EXTENSIONS,
} from "./fileUploadConstants";

const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot === -1) return "";
  return fileName.slice(lastDot).toLowerCase();
};

const isNachaValidationError = (message: string): boolean =>
  message.startsWith("Invalid NACHA file:");

type ImportMode = "ach" | "ach_passthrough" | "wire" | null;

const getAllowedExtensions = (importMode: ImportMode): string[] => {
  if (importMode === "wire") return CSV_EXTENSIONS;
  return ALLOWED_EXTENSIONS;
};

const getAcceptAttribute = (importMode: ImportMode): string => {
  if (importMode === "ach_passthrough") return "";
  return getAllowedExtensions(importMode).join(",");
};

const getInvalidExtensionErrorKey = (importMode: ImportMode): string => {
  if (importMode === "wire")
    return "file-upload-error-invalid-extension-csv-only";
  return "file-upload-error-invalid-extension";
};

const getSupportingTextKey = (importMode: ImportMode): string => {
  if (importMode === "wire") return "file-upload-supporting-text-csv-only";
  if (importMode === "ach_passthrough")
    return "file-upload-supporting-text-nacha-only";
  return "file-upload-supporting-text";
};

const ACH_CSV_FIELDS: {
  labelKey: string;
  required: boolean;
}[] = [
  {
    labelKey: "file-upload-csv-field-recipient-name",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-account",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-routing",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-amount",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-date",
    required: false,
  },
  {
    labelKey: "file-upload-csv-field-action",
    required: false,
  },
  {
    labelKey: "file-upload-csv-field-description",
    required: false,
  },
  {
    labelKey: "file-upload-csv-field-recipient-account-type",
    required: false,
  },
];
const WIRE_CSV_FIELDS: {
  labelKey: string;
  required: boolean;
}[] = [
  {
    labelKey: "file-upload-csv-field-recipient-name",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-account",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-routing",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-street-address",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-city",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-state",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-recipient-zip-code",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-wire-reason",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-amount",
    required: true,
  },
  {
    labelKey: "file-upload-csv-field-memo",
    required: true,
  },
];

export const ACH_IMPORT_SAMPLE_CSV = [
  [
    "Recipient Name",
    "Account Number",
    "Routing Number",
    "Amount",
    "Date",
    "Action",
    "Description",
    "Recipient Account Type",
  ],
  [
    "Acme Supplies Co.",
    "123456789",
    "021000021",
    "1250.00",
    "06/15/2026",
    "send",
    "Monthly office supply order",
    "checking",
  ],
]
  .map((row) => row.join(","))
  .join("\n");

const ACH_IMPORT_SAMPLE_CSV_FILENAME = "sample_payment_import.csv";

const FILE_TYPE_DESCRIPTIONS: Record<
  "ach" | "ach_passthrough" | "wire",
  { titleKey: string; descriptionKey: string }
> = {
  ach: {
    titleKey: "file-upload-type-ach-title",
    descriptionKey: "file-upload-file-types-ach-description",
  },
  ach_passthrough: {
    titleKey: "file-upload-type-passthrough-title",
    descriptionKey: "file-upload-file-types-passthrough-description",
  },
  wire: {
    titleKey: "file-upload-type-wire-title",
    descriptionKey: "file-upload-file-types-wire-description",
  },
};

const FileTypesLearnMoreModal = ({
  isOpen,
  onClose,
  modes,
}: {
  isOpen: boolean;
  onClose: () => void;
  modes: ("ach" | "ach_passthrough" | "wire")[];
}) => {
  const { l10n } = useLocalization();

  return (
    <Dialog
      isOpen={isOpen}
      title={l10n.getString("file-upload-file-types-modal-title")}
      onUserDismiss={onClose}
      headerStyle="bordered"
      width="500px"
    >
      {modes.map((mode) => (
        <div className="margin--top--l" key={mode}>
          <span className="fontWeight--bold">
            {l10n.getString(FILE_TYPE_DESCRIPTIONS[mode].titleKey)}
          </span>
          <div className="margin--top--xs">
            <span className="fontSize--s">
              {mode === "ach_passthrough" && !modes.includes("ach")
                ? l10n.getString(
                    "file-upload-file-types-passthrough-description-without-ach"
                  )
                : l10n.getString(FILE_TYPE_DESCRIPTIONS[mode].descriptionKey)}
            </span>
          </div>
        </div>
      ))}
    </Dialog>
  );
};

const LearnMoreModal = ({
  isOpen,
  onClose,
  isWires,
}: {
  isOpen: boolean;
  onClose: () => void;
  isWires: boolean;
}) => {
  const { l10n } = useLocalization();
  const wireReasons = modules.wireTemplates.useWireReasons();

  const csvFields = isWires ? WIRE_CSV_FIELDS : ACH_CSV_FIELDS;
  return (
    <Dialog
      isOpen={isOpen}
      title={l10n.getString("file-upload-learn-more-title")}
      onUserDismiss={onClose}
      headerStyle="bordered"
      width="627px"
    >
      <div className="margin--top--l">
        <span className="fontWeight--bold">
          {l10n.getString(
            isWires
              ? "file-upload-wire-csv-requirements"
              : "file-upload-csv-requirements"
          )}
        </span>
      </div>
      <div className="margin--top--s">
        {csvFields.map(({ labelKey, required }) => (
          <div
            key={labelKey}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "var(--space-m) 0",
              borderBottom: "1px solid var(--border-color-default)",
            }}
          >
            <span className="fontSize--s">{l10n.getString(labelKey)}</span>

            {isWires ? (
              <Chip
                kind={required ? "primary" : "info"}
                label={l10n.getString(
                  required
                    ? "file-upload-csv-required"
                    : "file-upload-csv-optional"
                )}
              />
            ) : (
              <Chip
                kind={required ? "info" : "secondary"}
                label={l10n.getString(
                  required
                    ? "file-upload-csv-required"
                    : "file-upload-csv-optional"
                )}
              />
            )}
          </div>
        ))}
      </div>
      {isWires ? (
        <>
          <div className="margin--top--l">
            <span className="fontSize--s">
              {l10n.getString("file-upload-wire-csv-note")}
            </span>
          </div>
          <p
            className="fontColor--secondary fontSize--xs fontWeight--bold padding--top--xs margin--top--l margin--bottom--m"
            style={{ borderTop: "1px solid var(--border-color-default)" }}
          >
            {l10n.getString("file-upload-accepted-wire-reasons")}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--space-xs)",
            }}
          >
            {wireReasons.map((reason) => (
              <Chip
                key={reason}
                kind="secondary"
                label={l10n.getString(reason)}
                hasBorder={true}
              />
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="margin--top--l">
            <span className="fontSize--s">
              {l10n.getString("file-upload-csv-note")}
            </span>
          </div>
          <div
            className="margin--top--l"
            style={{
              borderTop: "1px solid var(--border-color-default)",
              paddingTop: "var(--space-l)",
            }}
          >
            <span className="fontWeight--bold">
              {l10n.getString("file-upload-nacha-requirements")}
            </span>
            <div className="margin--top--s">
              <span className="fontSize--s">
                {l10n.getString("file-upload-nacha-note")}
              </span>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
};

const FileUploadScreen = () => {
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  const { sendNotification } = useNotificationContext();
  const {
    nachaResponse,
    setNachaResponse,
    setCsvResponse,
    clearImport: clearACHImport,
    importType,
  } = useACHImportContext();
  const { setCsvResponse: setWireCsvResponse, clearImport: clearWireImport } =
    useDomesticWireImportContext();
  const {
    setValidateResponse: setPassthroughValidateResponse,
    clearValidation: clearPassthroughValidation,
  } = useACHPassthroughImportContext();
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [passthroughFile, setPassthroughFile] = useState<File | null>(null);
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const [isFileTypesLearnMoreOpen, setIsFileTypesLearnMoreOpen] =
    useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [modeError, setModeError] = useState<string | null>(null);
  const latestRequestId = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const preselectMode = (
    location.state as { preselectMode?: ImportMode } | null
  )?.preselectMode;

  const achEnabled = Boolean(
    features.ach_file_import &&
      features.bulk_payments &&
      featureEnabled(features, {
        or: [
          "indigo.ACHPaymentViaImport_permission",
          "indigo.ACHPaymentCollectViaImport_permission",
        ],
      })
  );
  const wireEnabled = Boolean(features.domestic_wire_file_import);
  // Direction (send/collect) is enforced at validation, not here.
  const passthroughEnabled = canUseAchPassthrough(features);

  const MODE_OPTIONS: {
    mode: "ach" | "ach_passthrough" | "wire";
    enabled: boolean;
    labelKey: string;
  }[] = [
    {
      mode: "ach",
      enabled: achEnabled,
      labelKey: "file-upload-type-ach-title",
    },
    {
      mode: "ach_passthrough",
      enabled: passthroughEnabled,
      labelKey: "file-upload-type-passthrough-title",
    },
    {
      mode: "wire",
      enabled: wireEnabled,
      labelKey: "file-upload-type-wire-title",
    },
  ];
  const enabledModeOptions = MODE_OPTIONS.filter((option) => option.enabled);
  const showModeSelector = enabledModeOptions.length > 1;

  const getDefaultMode = (): "ach" | "ach_passthrough" | "wire" | null => {
    if (
      preselectMode &&
      enabledModeOptions.some((option) => option.mode === preselectMode)
    ) {
      return preselectMode;
    }
    if (showModeSelector) return null;
    return enabledModeOptions[0]?.mode ?? null;
  };

  const [importMode, setImportMode] = useState<
    "ach" | "ach_passthrough" | "wire" | null
  >(getDefaultMode);

  const handleModeChange = (mode: "ach" | "ach_passthrough" | "wire") => {
    latestRequestId.current += 1;
    setImportMode(mode);
    setModeError(null);
    setUploadedFileName(null);
    setUploadComplete(false);
    setPassthroughFile(null);
    setFileError(null);
    clearWireImport();
    clearACHImport();
    clearPassthroughValidation();
  };

  const sendRequest = useCallback(
    async (file: File) => {
      latestRequestId.current += 1;
      const requestId = latestRequestId.current;
      const isStale = () => requestId !== latestRequestId.current;
      setFileError(null);
      setUploadComplete(false);
      clearACHImport();
      clearWireImport();
      const extension = getFileExtension(file.name);

      if (importMode !== "ach_passthrough") {
        const allowedExts = getAllowedExtensions(importMode);

        if (!allowedExts.includes(extension)) {
          const errorText = l10n.getString(
            getInvalidExtensionErrorKey(importMode)
          );
          setUploadedFileName(null);
          setFileError(errorText);
          throw errorText;
        }
      }

      if (
        importMode !== "ach_passthrough" &&
        CSV_EXTENSIONS.includes(extension) &&
        file.size > MAX_CSV_IMPORT_FILE_SIZE_BYTES
      ) {
        const errorText = l10n.getString("file-upload-error-exceeds-size-csv");
        setUploadedFileName(null);
        setFileError(errorText);
        throw errorText;
      }

      const formData = new FormData();
      formData.append("file", file);

      if (importMode === "wire") {
        try {
          const response = await ApiHttp.fetch(
            "wire_import/csv/import/",
            { method: "POST" },
            formData
          );
          if (isStale()) return;
          setWireCsvResponse(response as API.WireImport.UploadCSV.Response);
          setUploadComplete(true);
        } catch (error) {
          if (isStale()) return;
          setUploadedFileName(null);
          const errorText = extractErrorMessages(
            error,
            l10n.getString("file-upload-error-generic")
          );
          setFileError(errorText);
          throw errorText;
        }
        return;
      }

      if (importMode === "ach_passthrough") {
        setPassthroughFile(file);
        setUploadComplete(true);
        return;
      }

      const isCSV = CSV_EXTENSIONS.includes(extension);
      const endpoint = isCSV
        ? "ach_import/csv/import/"
        : "ach_import/nacha/import/";

      try {
        const response = await ApiHttp.fetch(
          endpoint,
          { method: "POST" },
          formData
        );
        if (isStale()) return;

        if (isCSV) {
          setCsvResponse(response as API.ACHImport.CSVUploadResponse);
        } else {
          setNachaResponse(response as API.ACHImport.PaymentsResponse);
        }

        setUploadComplete(true);
      } catch (error) {
        if (isStale()) return;
        setUploadedFileName(null);
        const uploadErrorText = extractErrorMessages(
          error,
          l10n.getString("file-upload-error-generic")
        );
        const errorText = isNachaValidationError(uploadErrorText)
          ? l10n.getString("file-upload-error-nacha-validation")
          : uploadErrorText;
        setFileError(errorText);
        throw errorText;
      }
    },
    [
      importMode,
      l10n,
      clearACHImport,
      clearWireImport,
      setCsvResponse,
      setNachaResponse,
      setWireCsvResponse,
    ]
  );

  const handleClickNext = async () => {
    if (showModeSelector && !importMode) {
      setModeError(l10n.getString("file-upload-error-select-type"));
      return;
    }
    if (!uploadComplete) {
      setFileError(l10n.getString("file-upload-error-no-file"));
      return;
    }
    if (importMode === "ach_passthrough") {
      if (!passthroughFile) return;

      setFileError(null);
      const formData = new FormData();
      formData.append("file", passthroughFile);
      formData.append("title", passthroughFile.name.replace(/\.[^./]+$/, ""));

      try {
        const response = (await ApiHttp.fetch(
          "ach_nacha_passthrough/validate/",
          { method: "POST" },
          formData
        )) as API.ACHPassthrough.ValidateResponse;

        setPassthroughValidateResponse(response, passthroughFile.name);
        navigate(
          response.is_valid
            ? "passthrough/additional-details"
            : "passthrough/validation-results"
        );
      } catch (error) {
        const errorText = extractErrorMessages(
          error,
          l10n.getString("file-upload-error-generic")
        );
        setFileError(errorText);
      }
      return;
    }
    if (importMode === "wire") {
      navigate("wire/map-columns");
      return;
    }
    if (importType === "csv") {
      navigate("map-columns");
      return;
    }
    if (importType === "nacha") {
      const hasNewRecipients = nachaResponse?.rows.some(
        (row) => row.non_recipient_destination !== null
      );
      if (hasNewRecipients) {
        navigate("review-recipients");
      } else {
        navigate("additional-details");
      }
    }
  };

  const supportingTextKey = getSupportingTextKey(importMode);

  const handleAchSampleDownload = useCallback(() => {
    triggerFileDownload(ACH_IMPORT_SAMPLE_CSV, ACH_IMPORT_SAMPLE_CSV_FILENAME);
  }, []);

  const handleWireSampleDownload = useCallback(async () => {
    try {
      const response = await ApiHttp.fetch("wire_import/csv/sample/", {
        method: "GET",
        shouldGetBlob: true,
      });

      const filename = extractFilenameFromResponse(
        response.headers["content-disposition"],
        "csv"
      );
      triggerFileDownload(response.data, filename);
    } catch (error) {
      sendNotification({
        type: "negative",
        text: extractErrorMessages(
          error,
          l10n.getString("file-upload-error-generic")
        ),
      });
    }
  }, [l10n, sendNotification]);

  if (enabledModeOptions.length === 0) {
    return (
      <Container maxWidth="640px">
        <HeaderWithSteps
          useDisplayFont
          headerText={l10n.getString("file-upload-screen-combined-header")}
        />
        <div className="margin--top--xl">
          <ContentCard kind="bordered" paddingSize="l">
            <span className="fontSize--l fontWeight--bold">
              {l10n.getString("file-upload-empty-state-title")}
            </span>
            <div className="margin--top--s">
              <span className="fontSize--s fontColor--secondary">
                {l10n.getString("file-upload-empty-state-subtitle")}
              </span>
            </div>
          </ContentCard>
        </div>
        <div className="margin--bottom--xl margin--top--xl">
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                label={l10n.getString("button-back")}
                onClick={() => navigate(`/${PAYMENT_ACTIVITY_ROUTE}`)}
              />
            </Row.Item>
          </Row>
        </div>
      </Container>
    );
  }

  return (
    <Container maxWidth="640px">
      <HeaderWithSteps
        useDisplayFont
        headerText={l10n.getString("file-upload-screen-combined-header")}
      />
      {showModeSelector && (
        <div className="margin--top--xl">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span className="fontSize--l fontWeight--bold">
              {l10n.getString("file-upload-type-selector-label")}
            </span>
            <Button
              kind="plain"
              onClick={() => setIsFileTypesLearnMoreOpen(true)}
              label={l10n.getString("file-upload-file-types-learn-more")}
            />
          </div>
          <div
            className="margin--top--l"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-s)",
            }}
          >
            {enabledModeOptions.map((option) => (
              <div key={option.mode}>
                <Radio
                  kind="input-card"
                  name="importMode"
                  value={option.mode}
                  checked={importMode === option.mode}
                  onCheck={() => handleModeChange(option.mode)}
                >
                  <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
                    {l10n.getString(option.labelKey)}
                  </h4>
                </Radio>
              </div>
            ))}
          </div>
          {modeError && (
            <div className="margin--top--s">
              <span className="fontSize--s fontColor--error">{modeError}</span>
            </div>
          )}
        </div>
      )}
      {(importMode === "ach" ||
        importMode === "wire" ||
        importMode === "ach_passthrough") && (
        <div className="margin--top--l">
          <ContentCard kind="bordered" paddingSize="l">
            <span className="fontSize--l fontWeight--bold">
              {l10n.getString("file-upload-section-label")}
            </span>
            <div className="margin--top--l">
              <style>
                {`
                .ach-file-uploader #file-uploader-label {
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  min-height: 180px;
                  gap: var(--space-s);
                }
                .ach-file-uploader #file-uploader-label .narmi-icon-upload {
                  padding-right: 0;
                  font-size: var(--font-size-xl);
                }
                .ach-file-uploader #file-uploader-label .upload-button .fontColor--secondary {
                  display: block;
                  text-align: center;
                  margin-top: var(--space-xs);
                }
                .ach-file-uploader > .nds-input-error {
                  margin-top: var(--space-xs);
                }
              `}
              </style>
              <div className="ach-file-uploader">
                {importType === "nacha" &&
                  uploadComplete &&
                  nachaResponse?.warnings?.map((warningType) => (
                    <div
                      key={warningType}
                      className="margin--bottom--l"
                      data-testid={`file-upload-warning-${warningType}`}
                    >
                      <Alert isActive kind="warn" isDismissable={false}>
                        {l10n.getString(
                          `column-mapping-warning-${warningType.replace(
                            /_/g,
                            "-"
                          )}`,
                          nachaResponse.warning_context?.[warningType] ?? {}
                        )}
                      </Alert>
                    </div>
                  ))}
                <FileUploader
                  key={importMode}
                  sendRequest={sendRequest}
                  accept={getAcceptAttribute(importMode)}
                  // sendRequest surfaces every failure inline via fileError.
                  shouldSuppressErrorNotification={() => true}
                  onChange={(fileName: string | null) => {
                    setUploadedFileName(fileName);
                    if (!fileName) {
                      latestRequestId.current += 1;
                      setUploadComplete(false);
                    }
                  }}
                  value={uploadedFileName}
                  error={fileError}
                  expand
                  maxFileUploadSizeBytes={
                    importMode === "wire"
                      ? MAX_CSV_IMPORT_FILE_SIZE_BYTES
                      : MAX_ACH_IMPORT_FILE_SIZE_BYTES
                  }
                  messages={{
                    errorNoFile: l10n.getString("file-upload-error-no-file"),
                    errorMultipleUnsupported: l10n.getString(
                      "file-upload-error-multiple-unsupported"
                    ),
                    errorUnsupportedType: l10n.getString(
                      "file-upload-error-invalid-extension"
                    ),
                    errorFailedExceedsSize:
                      importMode === "wire"
                        ? l10n.getString("file-upload-error-exceeds-size-csv")
                        : l10n.getString("file-upload-error-exceeds-size"),
                    errorFailed: l10n.getString("file-upload-error-generic"),
                    label: l10n.getString("file-upload-label"),
                    maxSizeLabel: l10n.getString(supportingTextKey),
                  }}
                />
              </div>
            </div>
            {importMode !== "ach_passthrough" && (
              <div
                className="margin--top--l"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {importMode === "ach" ? (
                  <Button
                    kind="plain"
                    onClick={handleAchSampleDownload}
                    startIcon="download"
                    label={l10n.getString("file-upload-csv-sample")}
                  />
                ) : (
                  <Button
                    kind="plain"
                    onClick={handleWireSampleDownload}
                    startIcon="download"
                    label={l10n.getString("file-upload-csv-sample-wire")}
                  />
                )}
                <span
                  className="fontSize--s"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-xs)",
                  }}
                >
                  <span className="narmi-icon-help-circle" />
                  {l10n.getString("file-upload-learn-more-prompt")}{" "}
                  <Button
                    kind="plain"
                    onClick={() => setIsLearnMoreOpen(true)}
                    label={l10n.getString("file-upload-learn-more")}
                  />
                </span>
              </div>
            )}
          </ContentCard>
        </div>
      )}
      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="plain"
              label={l10n.getString("button-cancel")}
              onClick={() => {
                clearPassthroughValidation();
                navigate("/transfer");
              }}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleClickNext}
            />
          </Row.Item>
        </Row>
      </div>
      <LearnMoreModal
        isOpen={isLearnMoreOpen}
        onClose={() => setIsLearnMoreOpen(false)}
        isWires={importMode === "wire"}
      />
      <FileTypesLearnMoreModal
        isOpen={isFileTypesLearnMoreOpen}
        onClose={() => setIsFileTypesLearnMoreOpen(false)}
        modes={enabledModeOptions.map((option) => option.mode)}
      />
    </Container>
  );
};

export default FileUploadScreen;
